// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import {BufferAttribute, BufferGeometry, Mesh, Vector3, type Group, type Material} from 'three'
import {mergeGeometries} from 'three/addons/utils/BufferGeometryUtils.js'
import type source from '../../SourceArt/CabinetControlAssembly/mesh.json'
import {cabinetControlsFor, type ControlPoint} from '../xr/cabinetControls'
import type {CabinetDimensions} from './cabinet'
import {PartPalette, registerSlingSurfaces} from './partsKit'

let data: typeof source | undefined
export async function prepareCabinetControlAssembly(): Promise<void> {
  registerSlingSurfaces((await import('./slingSurfaces')).createSlingSurface)
  data ??= (await import('../../SourceArt/CabinetControlAssembly/mesh.json')).default
}

/** Proper rotations: authored +Z leaves the socket, opposite the gate's inward axis. */
function orient(v: Vector3, inward: ControlPoint): Vector3 {
  const {x, y, z} = v
  if (Math.abs(inward.x) > .5) return v.set(-inward.x * z, y, inward.x * x)
  if (inward.y < -.5) return v.set(x, z, -y)
  if (inward.y > .5) return v.set(x, -z, y)
  if (inward.z > .5) return v.set(-x, y, -z)
  return v
}

export function cabinetControlGeometry(radius: number, role: string, inward: ControlPoint): BufferGeometry | undefined {
  const part = data?.parts.find(p => p.role === role && Math.abs(p.radius - radius) < 1e-9)
  if (!part) return undefined
  const geometry = new BufferGeometry(), positions = new Float32Array(part.positions), normals = new Float32Array(part.normals)
  const v = new Vector3()
  for (let i = 0; i < positions.length; i += 3) {
    orient(v.fromArray(positions, i), inward).toArray(positions, i)
    orient(v.fromArray(normals, i), inward).toArray(normals, i)
  }
  geometry.setAttribute('position', new BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new BufferAttribute(normals, 3))
  geometry.setAttribute('uv', new BufferAttribute(new Float32Array(part.uvs), 2))
  geometry.setIndex(part.triangles)
  geometry.computeBoundingBox(); geometry.computeBoundingSphere()
  return geometry
}

/** Keep identities, gate reference points and the existing five-millimetre cap stroke. */
export function assembleCabinetControls(group: Group, dims: CabinetDimensions, fitted: boolean,
  geometries: BufferGeometry[], materials: Material[]): boolean {
  if (!data) return false
  const ids = ['flipperLeft', 'flipperRight', 'start', 'coin'] as const
  if (ids.some(id => !(group.getObjectByName(id) instanceof Mesh))) return false
  const controls = cabinetControlsFor(dims)
  const fixed: Record<string, BufferGeometry[]> = {bezel: [], gasket: []}
  for (const id of ids) {
    const mesh = group.getObjectByName(id) as Mesh, control = controls[id]
    const radius = id.startsWith('flipper') ? .06 : .03
    const geometry = cabinetControlGeometry(radius, 'cap', control.inward)!
    // The old cylinder remains in the owning build's disposal list. The new surface
    // is in the same local frame; moving the root still drives exactly one cap.
    mesh.geometry = geometry; mesh.quaternion.identity(); mesh.userData['authoredCabinetCap'] = true
    geometries.push(geometry)
    if (!fitted) for (const role of ['bezel', 'gasket']) {
      const part = cabinetControlGeometry(radius, role, control.inward)!
      part.translate(control.at.x, control.at.y, control.at.z); fixed[role]!.push(part)
    }
  }
  if (!fitted) {
    const palette = new PartPalette()
    for (const role of ['bezel', 'gasket']) {
      const pieces = fixed[role]!, geometry = mergeGeometries(pieces, false)
      pieces.forEach(p => p.dispose())
      if (!geometry) throw Error('Cannot merge control surrounds')
      const finish = role === 'bezel' ? 'apronSteel' : 'rubber'
      const mesh = new Mesh(geometry, palette.slingSurface(finish))
      mesh.name = `cabinetControls.${role}.component.authored.${finish}`
      group.add(mesh); geometries.push(geometry)
    }
    materials.push(...palette.all())
  }
  return true
}
